import React, { useState } from 'react';
import { useRecruitmentStatus } from '../../../shared/hooks/useRecruitmentStatus';

const DISCORD_URL = 'https://discord.gg/7wVa8EkvnP';

/**
 * Bannière publique « Recrutement ouvert » (page d'accueil).
 * Au clic : modale avec la marche à suivre (Discord + ticket) et un volet dépliable
 * par rôle recherché (intitulé + mission). Piloté depuis le panel (Founder + directions).
 */
const RecruitmentBanner: React.FC = () => {
  const { recruitment } = useRecruitmentStatus();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  if (!recruitment?.enabled) return null;

  const roles = recruitment.roles ?? [];

  const toggleVolet = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      {/* Bannière */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex w-full items-center justify-center gap-3 border-y border-emerald-500/40 bg-gradient-to-r from-emerald-600/25 via-emerald-500/20 to-emerald-600/25 px-4 py-2.5 text-sm font-semibold text-emerald-100 transition-colors hover:from-emerald-600/35 hover:to-emerald-600/35"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>
        <span>Recrutement ouvert — l'équipe cherche du renfort</span>
        <span className="rounded-full border border-emerald-400/50 bg-emerald-500/20 px-2 py-0.5 text-xs">
          Voir les postes{roles.length > 0 ? ` (${roles.length})` : ''}
        </span>
      </button>

      {/* Modale */}
      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-emerald-500/30 bg-[#0b1220] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-white"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-1 flex items-center gap-2">
              <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-semibold text-emerald-200">
                Recrutement
              </span>
            </div>
            <h2 className="mb-3 text-2xl font-bold text-white">L'équipe OpSecours recrute</h2>

            {recruitment.message && (
              <p className="mb-4 whitespace-pre-line text-sm text-slate-300">{recruitment.message}</p>
            )}

            {/* Marche à suivre */}
            <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="mb-3 text-sm font-semibold text-slate-200">Comment postuler ?</p>
              <ol className="space-y-2 text-sm text-slate-300">
                <li className="flex gap-2">
                  <span className="font-bold text-emerald-300">1.</span>
                  <span>
                    Rejoins notre serveur Discord.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-emerald-300">2.</span>
                  <span>Ouvre un ticket de candidature en précisant le poste visé.</span>
                </li>
              </ol>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4752c4]"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.2.36-.43.85-.59 1.24a18.27 18.27 0 0 0-3.94 0c-.16-.39-.4-.88-.6-1.24a19.74 19.74 0 0 0-3.76 1.37C2.53 8.13 1.73 11.79 2.05 15.4a19.9 19.9 0 0 0 5.99 3.03c.48-.66.91-1.36 1.28-2.1-.7-.26-1.37-.58-2-.96.17-.12.33-.25.49-.38a14.2 14.2 0 0 0 12.18 0c.16.14.32.26.49.38-.63.38-1.3.7-2 .96.37.74.8 1.44 1.28 2.1a19.86 19.86 0 0 0 5.99-3.03c.38-4.2-.65-7.83-2.72-11.03ZM8.68 13.3c-.9 0-1.64-.83-1.64-1.85 0-1.02.72-1.85 1.64-1.85.93 0 1.66.83 1.64 1.85 0 1.02-.72 1.85-1.64 1.85Zm6.64 0c-.9 0-1.64-.83-1.64-1.85 0-1.02.72-1.85 1.64-1.85.93 0 1.66.83 1.64 1.85 0 1.02-.71 1.85-1.64 1.85Z" />
                </svg>
                Rejoindre le Discord
              </a>
            </div>

            {/* Volets — un par rôle recherché */}
            {roles.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-200">
                  Postes recherchés ({roles.length})
                </p>
                <div className="space-y-2">
                  {roles.map((role) => {
                    const isOpen = expanded.has(role.id);
                    return (
                      <div
                        key={role.id}
                        className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]"
                      >
                        <button
                          type="button"
                          onClick={() => toggleVolet(role.id)}
                          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-white/[0.05]"
                        >
                          <span className="font-semibold text-white">{role.role_name}</span>
                          <svg
                            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isOpen && (
                          <div className="border-t border-white/10 px-4 py-3 text-sm text-slate-300">
                            {role.mission ? (
                              <p className="whitespace-pre-line">{role.mission}</p>
                            ) : (
                              <p className="italic text-slate-500">
                                Mission détaillée sur le Discord.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RecruitmentBanner;
